// עורך תבנית תיוג: תבנית משרד ודריסה ברמת קמפיין.
import { useEffect, useState } from 'react';
import { DEFAULT_TEMPLATE, type TrackingTemplate } from '@bb/schema';
import { api } from '../../api';
import { ErrorBox, Field, Loading } from '../ui';
import { errMsg, useAction } from './common';

export function parseTemplate(r: any): TrackingTemplate | null {
  if (!r) return null;
  const t = r.template ?? r;
  if (!t || typeof t.source !== 'string') return null;
  return { version: Number(t.version ?? r.version ?? 1), source: t.source, medium: t.medium, campaign: t.campaign, content: t.content };
}

export function useTemplates(campaignId: string) {
  const [office, setOffice] = useState<TrackingTemplate | null>(null);
  const [campaign, setCampaign] = useState<TrackingTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([api.get('/api/tracking-template'), api.get(`/api/tracking-template?campaignId=${campaignId}`)]).then(([o, c]) => {
      if (!alive) return;
      if (o.status === 'fulfilled') setOffice(parseTemplate(o.value)); else setError(errMsg(o.reason));
      if (c.status === 'fulfilled') {
        // השרת מסמן במפורש אם לקמפיין יש דריסה משלו
        setCampaign((c.value as any)?.isCampaignOverride ? parseTemplate(c.value) : null);
      }
      setLoading(false);
    });
    return () => { alive = false; };
  }, [campaignId, n]);
  return { office, campaign, effective: campaign ?? office ?? DEFAULT_TEMPLATE, error, loading, reload: () => setN((x) => x + 1) };
}

const FIELDS: { k: keyof Omit<TrackingTemplate, 'version'>; l: string }[] = [
  { k: 'source', l: 'utm_source' }, { k: 'medium', l: 'utm_medium' }, { k: 'campaign', l: 'utm_campaign' }, { k: 'content', l: 'utm_content' },
];

export function TemplateEditor({ campaignId, tpl, admin, onSaved }: { campaignId: string; tpl: ReturnType<typeof useTemplates>; admin: boolean; onSaved: () => void }) {
  const [scope, setScope] = useState<'office' | 'campaign'>(tpl.campaign ? 'campaign' : 'office');
  const base = scope === 'campaign' ? (tpl.campaign ?? tpl.office ?? DEFAULT_TEMPLATE) : (tpl.office ?? DEFAULT_TEMPLATE);
  const [form, setForm] = useState(base);
  useEffect(() => { setForm(base); }, [scope, tpl.office, tpl.campaign]); // eslint-disable-line react-hooks/exhaustive-deps
  const { busy, error, run } = useAction();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = { source: form.source, medium: form.medium, campaign: form.campaign, content: form.content };
    const url = scope === 'campaign' ? `/api/tracking-template?campaignId=${campaignId}` : '/api/tracking-template';
    if (await run(() => api.put(url, body), scope === 'campaign' ? 'דריסת הקמפיין נשמרה' : 'תבנית המשרד נשמרה')) { tpl.reload(); onSaved(); }
  };
  if (tpl.loading && !tpl.office) return <Loading />;
  return (
    <form className="stack" onSubmit={save} aria-label="תבנית תיוג">
      <ErrorBox error={tpl.error} />
      <div className="row">
        <label className="checkline"><input type="radio" name="tpl-scope" checked={scope === 'office'} onChange={() => setScope('office')} /> תבנית משרד (ברירת מחדל לכל הקמפיינים)</label>
        <label className="checkline"><input type="radio" name="tpl-scope" checked={scope === 'campaign'} onChange={() => setScope('campaign')} /> דריסה לקמפיין זה {tpl.campaign && <span className="badge warn">פעילה</span>}</label>
      </div>
      {scope === 'office' && !admin && <div className="alert warn small">תבנית משרד ניתנת לשינוי על ידי מנהל מערכת בלבד. ניתן להגדיר דריסה לקמפיין.</div>}
      <div className="small muted">משתנים זמינים: <span className="mono">{'{media} {campaign} {banner} {size} {variant}'}</span>. גרסה נוכחית: {base.version}. שינוי תבנית לא משנה ZIP שכבר יוצא.</div>
      <div className="grid cols-2">
        {FIELDS.map((f) => <Field key={f.k} label={f.l}><input className="ltr mono" required value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} /></Field>)}
      </div>
      <ErrorBox error={error} />
      <div className="row"><button className="btn primary" disabled={busy}>{busy ? 'שומר…' : 'שמירת תבנית'}</button><button type="button" className="btn" onClick={() => setForm({ ...DEFAULT_TEMPLATE, version: base.version })}>איפוס לברירת המחדל</button></div>
    </form>
  );
}
